import {
  FaMapMarkerAlt,
  FaRegClock,
  FaStar,
  FaPlane,
  FaUtensils,
  FaBed,
  FaCamera,
  FaSwimmer
} from "react-icons/fa";
import { IoMdArrowDropright, IoMdCheckmarkCircle } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageHero from "../../assets/zanzibar1.jpg";
import imageDay1 from "../../assets/zanzibar2.jpg";
import imageDay2 from "../../assets/znz_bch.jpg";
import imageDay3 from "../../assets/ZANZIBAR5.jpg";
import imageDay4 from "../../assets/mnemba.jpg";
import imageDay5 from "../../assets/zanzibar2.jpg";
import imageDay6 from "../../assets/zanz_excursion.jpg";
import imageDay7 from "../../assets/zanzibar1.jpg";

const hero = {
  kicker: "7 Days Zanzibar Adventure",
  title: "Stone Town to Nungwi",
  subtitle: "Complete Zanzibar experience from historic Stone Town to Nungwi beaches.",
  backgroundImage: imageHero,
  meta: ["7 Days", "Stone Town", "Nungwi Beach", "Spice Tour"],
};

const overview = `Visit Forodhani Gardens and sample street food like Zanzibari pizza and grilled seafood. Join a local spice guide to smell, taste, and learn how cloves, cinnamon, and nutmeg are grown and used. Visit a turtle rehabilitation center in Nungwi and see conservation work up close. Spend a morning in quiet souvenir markets before your departure, picking up crafts and fabrics unique to the island.`;

const highlights = [
  {
    icon: FaMapMarkerAlt,
    title: "Forodhani Gardens",
    description: "Sample street food like Zanzibari pizza and grilled seafood at this iconic night market.",
    color: "#10b981"
  },
  {
    icon: FaStar,
    title: "Spice Tour",
    description: "Learn how cloves, cinnamon, and nutmeg are grown and used with a local guide.",
    color: "#f59e0b"
  },
  {
    icon: FaPlane,
    title: "Prison Island",
    description: "Boat trip to Changuu Island to see Aldabra giant tortoises and snorkel clear waters.",
    color: "#3b82f6"
  },
  {
    icon: FaRegClock,
    title: "Turtle Sanctuary",
    description: "Visit Nungwi's rehabilitation center and see conservation efforts up close.",
    color: "#8b5cf6"
  },
];

const details = [
  {
    icon: FaMapMarkerAlt,
    title: "Route",
    description: "Stone Town > Prison Island > Spice Farm > Jozani > Nungwi Beach.",
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "7 days exploring Zanzibar's culture, nature, and beaches.",
  },
  {
    icon: FaUtensils,
    title: "Meals",
    description: "Daily breakfast included. Lunch & dinner on tour days.",
  },
  {
    icon: FaBed,
    title: "Accommodation",
    description: "New Amaan Hotel (Stone Town) & Hotel Riu Jambo (Nungwi).",
  },
];

const itinerary = [
  {
    dayLabel: "Day 1",
    dayNum: "01",
    title: "Arrive in Zanzibar and Explore Stone Town",
    image: imageDay1,
    body: "On arrival at Abeid Amani Karume International Airport, you are welcomed by our representative and transferred to New Amaan Hotel in Stone Town. The hotel offers incredible rooms with air-conditioning, private bathrooms, and city views. Each room includes a work desk, mini-bar, and free Wi-Fi. After a short briefing about the days ahead, you have the remainder of the day at leisure. A morning arrival allows more time to settle in, relax by the property's lounge areas, or step outside for a casual stroll through the narrow streets and waterfront close to the hotel.",
    activities: "Meals Plan: Bed and Breakfast",
    tags: ["Arrival", "Stone Town", "Leisure"]
  },
  {
    dayLabel: "Day 2",
    dayNum: "02",
    title: "Prison Island and Forodhani Gardens",
    image: imageDay2,
    body: "After breakfast, we take a short boat ride to Prison Island, also known as Changuu Island. The island was once used as a quarantine station, but it's best known for its sanctuary of Aldabra giant tortoises today, some of which are more than a century old. There is also time to walk along the beach, swim, or snorkel in the surrounding clear waters. Lunch is enjoyed at a local restaurant back in Stone Town before heading to Forodhani Gardens in the late afternoon. The gardens come alive in the evening with food stalls serving fresh seafood and Zanzibar's signature street dishes. Later, return to New Amaan Hotel for dinner and overnight.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["Boat Trip", "Wildlife", "Street Food"]
  },
  {
    dayLabel: "Day 3",
    dayNum: "03",
    title: "Spice Farm and Jozani Forest tour",
    image: imageDay3,
    body: "Your guide will be waiting at the hotel to take you out for a day that blends Zanzibar's spice heritage with its unique natural attractions. At the spice farms, you will walk through plantations that have given the island its title as the Spice Island, learning how cloves, cinnamon, nutmeg, and cardamom are grown and harvested. Depending on the season, you may be able to sample fruits straight from the trees and see how different spices are used in local cooking and traditional medicine. The afternoon continues at Jozani Chwaka Bay National Park, home to the rare red colobus monkey found only in Zanzibar. A walk through the forest brings encounters with the monkeys, mangrove swamps, and a range of birdlife. Lunch is served during the tour, and by late afternoon you return to New Amaan Hotel. The evening is free to relax at leisure.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["Spice Tour", "Wildlife", "Nature"]
  },
  {
    dayLabel: "Day 4",
    dayNum: "04",
    title: "Safari Blue Trip",
    image: imageDay4,
    body: "The Safari Blue trip is one of Zanzibar's signature experiences, taking place in the protected waters of Menai Bay. The day starts with a traditional dhow sailing across the lagoon, where dolphins are often spotted. Stops are made at sandbanks for swimming and snorkeling, with time to explore the rich marine life among the coral reefs. A highlight of the trip is the freshly prepared seafood barbecue served under the shade of the trees on Kwale Island, with fish, lobster, calamari, and tropical fruit. After lunch, there is time to relax, swim, or take a short walk to see the island's large baobab tree. Sailing back in the late afternoon captures the quiet side of coastal life. Dinner and overnight at New Amaan Hotel.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["Dhow Cruise", "Snorkeling", "Seafood"]
  },
  {
    dayLabel: "Day 5",
    dayNum: "05",
    title: "Stone Town to Nungwi Beach",
    image: imageDay5,
    body: "After breakfast, drive north to Nungwi, one of Zanzibar's best-known stretches of beach. On arrival, visit the local Turtle Rehabilitation Center to learn about ongoing conservation efforts and see endangered green turtles up close. Continue to Hotel Riu Jambo, set directly on the white sands of Nungwi. The property features spacious rooms with ocean views, several dining venues, and large swimming pools surrounded by palm trees. Lunch is served at the hotel, followed by free time to relax by the pool or on the beach with the Indian Ocean just steps away. The afternoon is left open for swimming or simply enjoying the resort facilities. Dinner is served in the evening before settling in for an overnight stay.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["Transfer", "Turtle Center", "Beach Resort"]
  },
  {
    dayLabel: "Day 6",
    dayNum: "06",
    title: "Nungwi Beach – Full day",
    image: imageDay6,
    body: "After breakfast at Hotel Riu Jambo, we travel to Mnemba Atoll for a day of exploration in one of Zanzibar's clearest waters. The atoll offers opportunities for snorkeling among coral reefs, spotting colorful fish, and observing marine life. The hotel can arrange kayaking, paddleboarding, or additional snorkeling trips without advance booking. Lunch is taken on the boat or at a nearby beach, allowing time to relax and enjoy the surroundings before continuing the afternoon activities. The afternoon continues with more time in the water or on the sand, taking in the calm of the atoll and its remote feel. Return to Hotel Riu Jambo in the late afternoon for some downtime before dinner. The evening is spent at the hotel enjoying the beach, pools, or other resort amenities.",
    activities: "Meals Plan: Breakfast, Lunch, Dinner",
    tags: ["Mnemba Atoll", "Snorkeling", "Water Sports"]
  },
  {
    dayLabel: "Day 7",
    dayNum: "07",
    title: "Departure – Nungwi to Airport",
    image: imageDay7,
    body: "Following breakfast at the hotel, there is a final chance to enjoy some time on Nungwi Beach before your flight. You can take a short swim, stroll along the shore, or relax by the hotel pool. If you prefer a brief activity, the hotel can arrange a quick visit to the local village or some last-minute souvenir shopping before transferring to the airport for your departure. Lunch is served at the hotel, giving time to pack and settle any final details. When ready, our representative will transfer you to Abeid Amani Karume International Airport for your onward flight, completing your week in Zanzibar with a mix of coastal relaxation and easy morning activities.",
    activities: "Meals Plan: Breakfast and Lunch",
    tags: ["Departure", "Beach Time", "Souvenirs"]
  },
];

export default function StoneTownAdventure() {
  const appContext = useContext(AppContext);
  // Color theme: Emerald/Teal for island vibe
  const theme = {
    primary: "#059669",
    primaryLight: "#10b981",
    accent: "#0d9488",
    bg: "#f8fafc",
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  return (
    <div style={{ 
      maxWidth: "1400px", 
      margin: "0 auto", 
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      color: theme.text,
      background: theme.bg
    }}>
      
      {/* ================= HERO SECTION ================= */}
      <section
        style={{
          height: "75vh",
          minHeight: "500px",
          backgroundImage: `linear-gradient(135deg, rgba(3, 8, 16, 0.85), rgba(5, 95, 105, 0.75)), url(${imageHero})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center",
          padding: "0 1.5rem",
          position: "relative"
        }}
      >
        <div style={{ maxWidth: "800px", animation: "fadeInUp 0.8s ease-out" }}>
          <span 
            className="badge rounded-pill px-4 py-2 mb-4"
            style={{ 
              background: "rgba(16,185,129,0.2)", 
              border: "1px solid rgba(16,185,129,0.4)",
              color: "#6ee7b7",
              fontSize: "0.9rem",
              fontWeight: "600",
              letterSpacing: "0.5px"
            }}
          >
            {hero.kicker}
          </span>
          
          <h1 
            style={{ 
              fontSize: "clamp(2.5rem, 6vw, 4rem)", 
              marginBottom: "1rem", 
              lineHeight: 1.1,
              fontWeight: "700",
              textShadow: "0 2px 10px rgba(0,0,0,0.3)"
            }}
          >
            {hero.title}
          </h1>
          
          <p 
            style={{ 
              fontSize: "clamp(1.1rem, 2.5vw, 1.35rem)", 
              marginBottom: "2.5rem",
              opacity: 0.95,
              lineHeight: 1.6
            }}
          >
            {hero.subtitle}
          </p>
          
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span 
                key={i} 
                style={{ 
                  background: "rgba(255,255,255,0.15)", 
                  padding: "0.5rem 1.25rem", 
                  borderRadius: "50px",
                  fontSize: "0.9rem",
                  fontWeight: "500",
                  backdropFilter: "blur(4px)"
                }}
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 1.5rem" }}>
        
        {/* ================= OVERVIEW ================= */}
        <section style={{ marginBottom: "5rem", textAlign: "center", maxWidth: "800px", margin: "0 auto 5rem" }}>
          <h2 
            style={{ 
              fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
              marginBottom: "1.5rem", 
              color: theme.text,
              fontWeight: "700"
            }}
          >
            Experience Zanzibar
          </h2>
          <p 
            style={{ 
              fontSize: "1.1rem", 
              lineHeight: 1.8, 
              color: theme.textMuted,
              marginBottom: "2rem"
            }}
          >
            {overview}
          </p>
          
          {/* Quick Stats */}
          <div className="row g-3 justify-content-center" style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {details.map((d, i) => {
              const DIcon = d.icon;
              return (
                <div 
                  key={i}
                  className="col-6 col-md-3"
                  style={{
                    background: theme.cardBg,
                    padding: "1rem 1.5rem",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    minWidth: "200px"
                  }}
                >
                  <DIcon size={22} style={{ color: theme.primary, flexShrink: 0 }} />
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: "600", color: theme.text }}>{d.title}</div>
                    <div style={{ fontSize: "0.85rem", color: theme.textMuted }}>{d.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ================= HIGHLIGHTS GRID ================= */}
        <section style={{ marginBottom: "5rem" }}>
          <div className="text-center mb-4">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Trip Highlights
            </h2>
            <p style={{ color: theme.textMuted }}>Unforgettable moments awaiting you</p>
          </div>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "1.5rem" 
          }}>
            {highlights.map((h, i) => {
              const HIcon = h.icon;
              return (
                <article 
                  key={i} 
                  style={{ 
                    background: theme.cardBg,
                    padding: "1.75rem",
                    borderRadius: "16px",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    cursor: "default",
                    border: "1px solid rgba(0,0,0,0.04)"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.08)";
                  }}
                >
                  <div 
                    className="d-inline-flex align-items-center justify-content-center rounded-2 mb-3"
                    style={{
                      width: "52px",
                      height: "52px",
                      background: `${h.color}15`,
                      color: h.color,
                      fontSize: "1.4rem"
                    }}
                  >
                    <HIcon />
                  </div>
                  <h3 style={{ fontSize: "1.25rem", marginBottom: "0.75rem", fontWeight: "600", color: theme.text }}>
                    {h.title}
                  </h3>
                  <p style={{ color: theme.textMuted, lineHeight: 1.6, fontSize: "0.95rem", margin: 0 }}>
                    {h.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        {/* ================= ITINERARY TIMELINE ================= */}
        <section>
          <div className="text-center mb-5">
            <h2 
              style={{ 
                fontSize: "clamp(1.75rem, 4vw, 2.25rem)", 
                marginBottom: "0.5rem", 
                color: theme.text,
                fontWeight: "700"
              }}
            >
              Your 7-Day Journey
            </h2>
            <p style={{ color: theme.textMuted }}>Day-by-day breakdown of your adventure</p>
          </div>

          <div style={{ position: "relative" }}>
            {/* Timeline connector line (desktop) */}
            <div 
              className="d-none d-lg-block"
              style={{
                position: "absolute",
                left: "50%",
                top: "0",
                bottom: "0",
                width: "2px",
                background: `linear-gradient(to bottom, ${theme.primary}33, ${theme.accent}33, transparent)`,
                transform: "translateX(-50%)"
              }}
            />

            {itinerary.map((day, i) => (
              <article 
                key={i} 
                className={`row g-4 align-items-center mb-4 ${i % 2 === 0 ? 'flex-lg-row' : 'flex-lg-row-reverse'}`}
                style={{
                  background: theme.cardBg,
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(0,0,0,0.04)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  position: "relative",
                  zIndex: 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-3px)";
                  e.currentTarget.style.boxShadow = "0 14px 35px rgba(0,0,0,0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.08)";
                }}
              >
                {/* Day Number Badge */}
                <div 
                  className="position-absolute top-0 start-0 m-3 rounded-pill px-3 py-1"
                  style={{
                    background: theme.primary,
                    color: "white",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    zIndex: 2,
                    boxShadow: "0 4px 10px rgba(5, 150, 105, 0.3)"
                  }}
                >
                  Day {day.dayNum}
                </div>

                {/* Image Column */}
                <div className="col-lg-5" style={{ position: "relative", minHeight: "280px" }}>
                  <div
                    style={{
                      height: "100%",
                      minHeight: "280px",
                      backgroundImage: `url(${day.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />
                  {/* Gradient overlay for text readability on image */}
                  <div 
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(15,23,42,0.7), transparent 60%)"
                    }}
                  />
                </div>

                {/* Content Column */}
                <div className="col-lg-7 p-4 p-lg-5">
                  <h3 
                    style={{ 
                      fontSize: "1.4rem", 
                      marginBottom: "1rem", 
                      fontWeight: "700",
                      color: theme.text,
                      paddingRight: "60px"
                    }}
                  >
                    {day.title}
                  </h3>
                  
                  <p style={{ lineHeight: 1.7, color: theme.textMuted, marginBottom: "1.25rem", fontSize: "0.95rem" }}>
                    {day.body}
                  </p>
                  
                  {/* Tags */}
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
                    {day.tags.map((tag, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: `${theme.primary}10`,
                          color: theme.primary,
                          padding: "0.35rem 0.85rem",
                          borderRadius: "50px",
                          fontSize: "0.8rem",
                          fontWeight: "500"
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Meals Info */}
                  <div 
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.6rem 1rem",
                      background: `${theme.accent}10`,
                      borderRadius: "8px",
                      fontSize: "0.9rem",
                      color: theme.accent,
                      fontWeight: "500"
                    }}
                  >
                    <FaUtensils size={14} />
                    {day.activities}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ================= CTA SECTION ================= */}
        <section 
          className="text-center mt-6 pt-5"
          style={{ 
            borderTop: `1px solid rgba(0,0,0,0.06)`,
            background: `linear-gradient(135deg, ${theme.primary}08, ${theme.accent}08)`,
            borderRadius: "20px",
            padding: "3rem 2rem",
            marginTop: "4rem"
          }}
        >
          <FaCamera size={42} style={{ color: theme.primary, marginBottom: "1rem" }} />
          <h3 
            style={{ 
              fontSize: "clamp(1.5rem, 4vw, 2rem)", 
              fontWeight: "700", 
              marginBottom: "1rem",
              color: theme.text
            }}
          >
            Ready for Your Zanzibar Adventure?
          </h3>
          <p style={{ color: theme.textMuted, maxWidth: "600px", margin: "0 auto 2rem", lineHeight: 1.6 }}>
            Book your 7-day journey from Stone Town to Nungwi Beach. Limited spots available for peak seasons.
          </p>
          <button 
            className="btn btn-lg px-5 py-3 rounded-pill"
            style={{ 
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryLight})`,
              border: "none",
              fontWeight: "600",
              color: "white",
              boxShadow: `0 6px 20px ${theme.primary}40`,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "transform 0.2s ease, box-shadow 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 8px 25px ${theme.primary}50`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = `0 6px 20px ${theme.primary}40`;
            }}
            onClick={() => appContext.navTo({ item: 'plan-trip' })}
          >
            Book This Trip <IoMdArrowDropright size={20} />
          </button>
        </section>

      </div>

      {/* ================= CSS ANIMATIONS ================= */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { transition: none !important; animation: none !important; }
        }
        @media (max-width: 991px) {
          [style*="left: 50%"] { display: none !important; }
        }
      `}</style>
    </div>
  );
}